import React, { useState, useEffect } from 'react';
import Confetti from 'react-confetti';
import { API_URL } from '../config';

const Quiz = () => {
  const [questions, setQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [score, setScore] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [loading, setLoading] = useState(false);
  const [quizComplete, setQuizComplete] = useState(false);
  const [streak, setStreak] = useState(0);
  const [lives, setLives] = useState(3);
  
  const subject = localStorage.getItem('currentSubject');
  const studyGuide = JSON.parse(localStorage.getItem('studyGuide') || '{}');

  useEffect(() => {
    if (studyGuide.topics && studyGuide.topics.length > 0) {
      loadQuestions();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadQuestions = async () => {
    setLoading(true);
    try {
      // Get a random topic from the study guide
      const randomTopic = studyGuide.topics[Math.floor(Math.random() * studyGuide.topics.length)];
      
      const response = await fetch(`${API_URL}/api/generate-questions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          topic: `${subject} - ${randomTopic.name}`,
          difficulty: 'intermediate',
          count: 10
        })
      });
      const data = await response.json();

      setQuestions(data);
    } catch (error) {
      console.error('Error loading questions:', error);
      alert('Failed to load questions. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerSelect = (answerIndex) => {
    if (selectedAnswer !== null) return;

    setSelectedAnswer(answerIndex);
    setShowExplanation(true);

    if (answerIndex === questions[currentQuestion].correctAnswer) {
      setScore(score + 100);
      setStreak(streak + 1);
      
      // Bonus points for streaks
      if (streak >= 3) {
        setScore(score + 150); // Extra 50 points for streak
      }
    } else {
      setStreak(0);
      setLives(lives - 1);
      
      if (lives <= 1) {
        setTimeout(() => {
          setQuizComplete(true);
        }, 2000);
      }
    }
  };

  const handleNextQuestion = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      setSelectedAnswer(null);
      setShowExplanation(false);
    } else {
      setQuizComplete(true);
    }
  };

  const restartQuiz = () => {
    // Update topic progress
    const currentSubject = localStorage.getItem('currentSubject');
    if (currentSubject) {
      const studiedTopics = JSON.parse(localStorage.getItem('studiedTopics') || '[]');
      const topicIndex = studiedTopics.findIndex(t => t.name === currentSubject);
      if (topicIndex !== -1) {
        studiedTopics[topicIndex].quizzesTaken = (studiedTopics[topicIndex].quizzesTaken || 0) + 1;
        studiedTopics[topicIndex].xpEarned = (studiedTopics[topicIndex].xpEarned || 0) + score;
        localStorage.setItem('studiedTopics', JSON.stringify(studiedTopics));
      }
    }

    setCurrentQuestion(0);
    setScore(0);
    setSelectedAnswer(null);
    setShowExplanation(false);
    setQuizComplete(false);
    setStreak(0);
    setLives(3);
    loadQuestions();
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p>Loading questions...</p>
      </div>
    );
  }

  if (questions.length === 0) {
    return (
      <div className="quiz-container">
        <p>No questions available. Please go back and select a subject.</p>
      </div>
    );
  }

  if (quizComplete) {
    const percentage = Math.round((score / (questions.length * 100)) * 100);
    const passed = percentage >= 70;
    
    return (
      <div className="quiz-container">
        {passed && <Confetti />}
        <div className="quiz-complete">
          <h1>{passed ? '🎉 Congratulations!' : '📚 Keep Practicing!'}</h1>
          <div className="final-score">
            <div className="stat-card">
              <span className="stat-value">{score}</span>
              <span className="stat-label">Final Score</span>
            </div>
            <div className="stat-card">
              <span className="stat-value">{percentage}%</span>
              <span className="stat-label">Accuracy</span>
            </div>
            <div className="stat-card">
              <span className="stat-value">{Math.max(...[streak, 0])}</span>
              <span className="stat-label">Best Streak</span>
            </div>
          </div>
          <button className="btn btn-primary" onClick={restartQuiz}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const question = questions[currentQuestion];
  const progress = ((currentQuestion + 1) / questions.length) * 100;

  return (
    <div className="quiz-container">
      <div className="quiz-header">
        <div>
          <h3>{subject}</h3>
          <p>Question {currentQuestion + 1} of {questions.length}</p>
        </div>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <div>
            <span style={{ fontSize: '1.2rem' }}>❤️</span> × {lives}
          </div>
          <div>
            <span style={{ fontSize: '1.2rem' }}>🔥</span> {streak}
          </div>
          <div>
            <span style={{ fontSize: '1.2rem' }}>💰</span> {score}
          </div>
        </div>
      </div>

      <div className="progress-bar">
        <div className="progress-fill" style={{ width: `${progress}%` }}></div>
      </div>

      <div className="question-card">
        <h2>{question.question}</h2>
        
        <div className="options-list">
          {question.options.map((option, index) => (
            <button
              key={index}
              className={`option-button ${
                selectedAnswer !== null &&
                (index === question.correctAnswer ? 'correct' : 
                 index === selectedAnswer ? 'incorrect' : '')
              }`}
              onClick={() => handleAnswerSelect(index)}
              disabled={selectedAnswer !== null}
            >
              {option}
            </button>
          ))}
        </div>

        {showExplanation && (
          <div style={{ 
            marginTop: '1.5rem', 
            padding: '1rem', 
            background: selectedAnswer === question.correctAnswer ? '#d4edda' : '#f8d7da',
            borderRadius: '8px'
          }}>
            <p><strong>{selectedAnswer === question.correctAnswer ? '✅ Correct!' : '❌ Incorrect'}</strong></p>
            <p>{question.explanation}</p>
          </div>
        )}
      </div>

      {showExplanation && (
        <button className="btn btn-primary" onClick={handleNextQuestion}>
          {currentQuestion < questions.length - 1 ? 'Next Question' : 'Finish Quiz'}
        </button>
      )}
    </div>
  );
};

export default Quiz;